import type { CSSProperties } from "react";
import { type FortressCard, isBuildingPatternAsymmetric } from "@fortress/shared";
import { ResourceIcon } from "../resources";
import { MirrorPair, PatternDiagram } from "./fortressPattern";

// The full visual of a single Fortress card — name, deck/timing badges, the
// build pattern (and its mirror), Obsidian cost and rules text. Shared by the
// read-only catalogue (`FortressLibrary`) and the in-game market detail popover
// (`App`), so the two always render a card the same way.

// Category colour schemes, shared by the sidebar sections (App) and this card
// face so the detail popover always matches the category it came from.
// Win = grey/black, Regular = light grey. Chosen to read on both the dark
// sidebar (as the accent line) and the white card face (as the top border).
export const WIN_ACCENT = "#4a4a52";
export const REGULAR_ACCENT = "#c7c7cf";

// Sub-label colours: the accents above are tuned for borders; on the white card
// these need a touch more contrast to stay legible as text.
const WIN_LABEL = "#33333a";
const REGULAR_LABEL = "#6f6f78";

const TIMING_LABEL: Record<string, string> = {
  "end-of-round": "EOR",
  "start-of-turn": "SOT",
  immediate: "IMM",
};
const TIMING_TITLE: Record<string, string> = {
  "end-of-round": "Resolves at end of round",
  "start-of-turn": "Resolves at start of turn",
  immediate: "Resolves immediately",
};

export function FortressCardFace({
  card,
  style,
}: {
  card: FortressCard;
  /** Merged onto the card's outer container (positioning, width overrides). */
  style?: CSSProperties;
}) {
  const isWin = card.kind === "win";
  const accent = isWin ? WIN_ACCENT : REGULAR_ACCENT;
  const obsidian = card.cost[3];
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        border: "1px solid #ddd5c2",
        borderTop: `3px solid ${accent}`,
        borderRadius: 12,
        padding: 16,
        background: "#fff",
        // Pin the text colour: this face is also rendered inside the dark
        // detail popover, where it would otherwise inherit the light body
        // colour and leave the card name illegible on the white background.
        color: "#2b2620",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <strong style={{ fontSize: 16 }}>{card.name}</strong>
        {card.winTiming && (
          <span
            title={TIMING_TITLE[card.winTiming]}
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.5,
              padding: "1px 6px",
              borderRadius: 5,
              background: "#2b2620",
              color: "#fff",
            }}
          >
            {TIMING_LABEL[card.winTiming] ?? card.winTiming}
          </span>
        )}
        <span
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 22,
            fontWeight: 700,
            color: "#2b2620",
            whiteSpace: "nowrap",
          }}
          title="Obsidian cost"
        >
          <ResourceIcon kind="obsidian" size={34} /> {obsidian}
        </span>
      </div>
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: isWin ? WIN_LABEL : REGULAR_LABEL,
          marginTop: 4,
          fontWeight: 600,
        }}
      >
        {isWin ? "Win Fortress" : "Regular Fortress"}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          background: "#faf6ea",
          border: "1px solid #e9e1cc",
          borderRadius: 8,
          padding: 6,
          margin: "10px 0 0",
        }}
      >
        {/* Only chiral patterns have a distinct mirror build orientation; for
            achiral ones the "mirror" is just a rotation, so a single diagram
            says it all. */}
        {isBuildingPatternAsymmetric(card.pattern) ? (
          <MirrorPair pattern={card.pattern} />
        ) : (
          <PatternDiagram pattern={card.pattern} maxW={150} maxH={130} />
        )}
      </div>
      <p
        style={{
          fontSize: 13,
          lineHeight: 1.45,
          color: "#3a342c",
          margin: "10px 0 0",
        }}
      >
        {card.description}
      </p>
    </div>
  );
}
